import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider
} from '@mui/material';
import { AlignmentSettings } from '../../types/pattern';
import { validSectors } from '../../utils/alignmentUtils';
import { useLocalization } from '../../localization/LocalizationContext';

interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (settings: AlignmentSettings) => void;
  settings: AlignmentSettings;
  onSettingsChange: (settings: AlignmentSettings) => void;
}

// Find the nearest valid sector count
const findNearestValidSectors = (value: number): number => {
  return validSectors.reduce((prev, curr) => {
    return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
  });
};

export const NewProjectDialog: React.FC<NewProjectDialogProps> = ({
  open,
  onClose,
  onConfirm,
  settings,
  onSettingsChange
}) => {
  const { t } = useLocalization();

  const handleSliderChange = (setting: keyof AlignmentSettings) => (
    event: Event,
    value: number | number[]
  ) => {
    if (setting === 'radialAngleStep') {
      const numericValue = Array.isArray(value) ? value[0] : value;
      const validSectorsCount = findNearestValidSectors(numericValue);
      const angleStep = 360 / validSectorsCount;
      onSettingsChange({
        ...settings,
        [setting]: angleStep
      });
    } else {
      onSettingsChange({
        ...settings,
        [setting]: value
      });
    }
  };

  // Calculate current sector count
  const currentSectors = Math.round(360 / settings.radialAngleStep);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t.newProject.title}</DialogTitle>
      <DialogContent>
        <Box sx={{ p: 2, userSelect: 'none' }}>

          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>
              {t.newProject.settings.wedges}: {currentSectors} 
            </Typography>
            <Slider
              value={currentSectors}
              onChange={handleSliderChange('radialAngleStep')}
              min={3}
              max={72}
              step={1}
              marks={validSectors
                .filter(sectors => {
                  if (sectors < 20) return sectors % 3 === 0;
                  if (sectors < 40) return sectors % 2 === 0;
                  return true;
                })
                .map(sectors => ({
                  value: sectors,
                  label: `${sectors}`
                }))}
              sx={{
                '& .MuiSlider-markLabel': {
                  fontSize: '0.75rem',
                  transform: 'translateX(-50%)',
                  '&:nth-of-type(odd)': {
                    transform: 'translateX(-50%) translateY(20px)'
                  }
                }
              }}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>
              {t.newProject.settings.circleStep}: {settings.circleStep}px
            </Typography>
            <Slider
              value={settings.circleStep}
              onChange={handleSliderChange('circleStep')}
              min={10}
              max={100}
              step={10}
              marks={[
                { value: 10, label: '10' },
                { value: 60, label: '60' },
                { value: 100, label: '100' }
              ]}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography gutterBottom>
              {t.newProject.settings.rows}: {settings.circlesCount}
            </Typography>
            <Slider
              value={settings.circlesCount}
              onChange={handleSliderChange('circlesCount')}
              min={5}
              max={65}
              step={5}
              marks={[
                { value: 5, label: '5' },
                { value: 35, label: '35' },
                { value: 65, label: '65' }
              ]}
            />
          </Box>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t.newProject.buttons.cancel}</Button>
        <Button onClick={() => onConfirm(settings)} variant="contained" color="primary">
          {t.newProject.buttons.create}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 